import { useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { blurbDefault, clean, stagger, galleryPhotos } from '../utils/quinn';
import { useMediaQuery, useRawProperty } from '../utils/hooks';
import { Lightbox, useLightbox } from '../utils/lightbox';
import { PATHS } from '../utils/site';

export interface Amenity {
  title?: string;
  group?: string;
  amenity_image?: string;
  is_featured?: string | null;
}

interface Photo {
  image?: string;
  alt_text?: string;
  description?: string;
}

const ctaDef: SlotGroupDefinition = {
  label: 'All Amenities Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Amenities' },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const photoDef: SlotGroupDefinition = {
  label: 'Section Photo',
  description: 'Shown beside the amenity list until a visitor picks an amenity. Until set, a gallery photo is chosen automatically.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: "A gallery photo that shows this property's shared amenities or community spaces at their best. Prefer a real amenity over a home interior or plain exterior.",
    context: 'Tall photo beside the shownAmenities-amenity list in the Amenities section of the home page, below the hero. The hero already uses the featured image, so pick a different photo.',
  },
};

const amenityPhoto1Def: SlotGroupDefinition = {
  label: 'Amenity 1 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 1st one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 1st amenity, leave every field empty.",
    context:
      "Photo slot 1 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto2Def: SlotGroupDefinition = {
  label: 'Amenity 2 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 2nd one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 2nd amenity, leave every field empty.",
    context:
      "Photo slot 2 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto3Def: SlotGroupDefinition = {
  label: 'Amenity 3 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 3rd one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 3rd amenity, leave every field empty.",
    context:
      "Photo slot 3 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto4Def: SlotGroupDefinition = {
  label: 'Amenity 4 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 4th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 4th amenity, leave every field empty.",
    context:
      "Photo slot 4 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto5Def: SlotGroupDefinition = {
  label: 'Amenity 5 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 5th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 5th amenity, leave every field empty.",
    context:
      "Photo slot 5 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto6Def: SlotGroupDefinition = {
  label: 'Amenity 6 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 6th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 6th amenity, leave every field empty.",
    context:
      "Photo slot 6 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto7Def: SlotGroupDefinition = {
  label: 'Amenity 7 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 6, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 7th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 7th amenity, leave every field empty.",
    context:
      "Photo slot 7 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto8Def: SlotGroupDefinition = {
  label: 'Amenity 8 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 7, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 8th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 8th amenity, leave every field empty.",
    context:
      "Photo slot 8 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto9Def: SlotGroupDefinition = {
  label: 'Amenity 9 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 9th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 9th amenity, leave every field empty.",
    context:
      "Photo slot 9 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto10Def: SlotGroupDefinition = {
  label: 'Amenity 10 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 9, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 10th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 10th amenity, leave every field empty.",
    context:
      "Photo slot 10 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto11Def: SlotGroupDefinition = {
  label: 'Amenity 11 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 10, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 11th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 11th amenity, leave every field empty.",
    context:
      "Photo slot 11 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

const amenityPhoto12Def: SlotGroupDefinition = {
  label: 'Amenity 12 Photo',
  description:
    "Photo for one amenity in the list: its row thumbnail, and the large photo when a visitor picks it. Amenity is the exact title of the amenity it belongs to, so the photo stays with that amenity if the list is reordered; leave it blank to use this slot for the next amenity without one. Until an image is set, the amenity's own image is used, else a gallery photo that names it, else an unused gallery photo.",
  fields: {
    amenity: {
      label: 'Amenity',
      type: 'text',
      defaultValue: '',
      generator: { prompt: "The amenity's title, copied exactly as it appears in the property's amenities list. Nothing else." },
    },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 11, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      "Read the property's amenities and take the 12th one that appears in this list. The list shows every amenity whose is_featured is '1' or 'true', in the order given. If none are featured, it shows up to six: first a yard or green space, garage, lawn or landscaping, smart home or thermostat, pool, playground, then dog park or pet amenity, then other Apartment-group and Community-group amenities in order. Set Amenity to that amenity's exact title. Set Image to its own amenity_image if it has one, else the gallery photo that most clearly shows that amenity, judged from the photo itself and its alt text and description. Alt text describes what the chosen photo shows. If the list has no 12th amenity, leave every field empty.",
    context:
      "Photo slot 12 of 12 for the Amenities list on the home page: a small thumbnail beside the amenity name, and a tall photo when a visitor picks that row. Each slot covers a different amenity, and no two slots or the Section Photo should use the same gallery photo.",
  },
};

/** Rows beyond the 12 photo slots keep the automatic photo. */
function wrapPhoto(slot: number | null, i: number, row: ReactNode) {
  switch (slot) {
    case 0:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_1" definition={amenityPhoto1Def}>{row}</EditableSlotGroup>;
    case 1:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_2" definition={amenityPhoto2Def}>{row}</EditableSlotGroup>;
    case 2:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_3" definition={amenityPhoto3Def}>{row}</EditableSlotGroup>;
    case 3:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_4" definition={amenityPhoto4Def}>{row}</EditableSlotGroup>;
    case 4:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_5" definition={amenityPhoto5Def}>{row}</EditableSlotGroup>;
    case 5:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_6" definition={amenityPhoto6Def}>{row}</EditableSlotGroup>;
    case 6:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_7" definition={amenityPhoto7Def}>{row}</EditableSlotGroup>;
    case 7:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_8" definition={amenityPhoto8Def}>{row}</EditableSlotGroup>;
    case 8:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_9" definition={amenityPhoto9Def}>{row}</EditableSlotGroup>;
    case 9:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_10" definition={amenityPhoto10Def}>{row}</EditableSlotGroup>;
    case 10:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_11" definition={amenityPhoto11Def}>{row}</EditableSlotGroup>;
    case 11:
      return <EditableSlotGroup key={i} groupPath="amenitieslist.amenity_photo_12" definition={amenityPhoto12Def}>{row}</EditableSlotGroup>;
    default:
      return <div key={i}>{row}</div>;
  }
}

/** Yardi's "Apartment" group reads as "Your home": Quinn rents houses. */
export const groupLabel = (g?: string) => (g === 'Apartment' ? 'Your home' : g || 'Community');

const PRIORITY = [/yard|green space/i, /garage/i, /lawn|landscap/i, /smart|thermostat/i, /pool/i, /playground/i, /dog park|pet/i];
const STOP = new Set(['home', 'unit', 'site', 'with', 'technology', 'maintenance', 'management', 'alternative', 'security', 'deposit', 'ready', 'cable', 'green', 'building', 'large', 'high', 'closet']);

/** An amenity's own photo, else the gallery photo whose alt text (then description) names it. */
export function amenityPhoto(a: Amenity, photos: Photo[]): { src: string; alt: string } | null {
  const own = clean(a.amenity_image);
  if (own) return { src: own, alt: a.title || '' };
  const words = (a.title || '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .map((w) => (w.length > 4 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w))
    .filter((w) => w.length >= 4 && !STOP.has(w));
  if (!words.length) return null;
  const hit = (field: 'alt_text' | 'description') => photos.find((ph) => words.some((w) => new RegExp('\\b' + w, 'i').test(ph[field] || '')));
  const g = hit('alt_text') || hit('description');
  return g?.image ? { src: g.image, alt: g.alt_text || a.title || '' } : null;
}

export function AmenitiesList() {
  const p = useRawProperty();
  const touch = useMediaQuery('(hover: none)');
  const lb = useLightbox();
  const cta = useSlotGroup('amenitieslist.cta', ctaDef);
  const photoSlot = useSlotGroup('amenitieslist.photo', photoDef);
  const photoSlots = [
    useSlotGroup('amenitieslist.amenity_photo_1', amenityPhoto1Def),
    useSlotGroup('amenitieslist.amenity_photo_2', amenityPhoto2Def),
    useSlotGroup('amenitieslist.amenity_photo_3', amenityPhoto3Def),
    useSlotGroup('amenitieslist.amenity_photo_4', amenityPhoto4Def),
    useSlotGroup('amenitieslist.amenity_photo_5', amenityPhoto5Def),
    useSlotGroup('amenitieslist.amenity_photo_6', amenityPhoto6Def),
    useSlotGroup('amenitieslist.amenity_photo_7', amenityPhoto7Def),
    useSlotGroup('amenitieslist.amenity_photo_8', amenityPhoto8Def),
    useSlotGroup('amenitieslist.amenity_photo_9', amenityPhoto9Def),
    useSlotGroup('amenitieslist.amenity_photo_10', amenityPhoto10Def),
    useSlotGroup('amenitieslist.amenity_photo_11', amenityPhoto11Def),
    useSlotGroup('amenitieslist.amenity_photo_12', amenityPhoto12Def),
  ];
  const all = useCollectionBinding<Amenity>('amenitieslist.items', { collection: 'amenities' });
  const [active, setActive] = useState<number | null>(null);

  const blurb = p.property_home_amenities_blurb;
  const titleDefault = blurbDefault(blurb, 'title');
  const leadDefault = blurbDefault(blurb, 'body');
  const photos = galleryPhotos<Photo>(p.photo_gallery);

  const amenities = all.filter((a) => clean(a.title));
  const featuredAmenities = amenities.filter((a) => a.is_featured === '1' || a.is_featured === 'true');
  const picked: Amenity[] = [];
  if (featuredAmenities.length) {
    picked.push(...featuredAmenities);
  } else {
    PRIORITY.forEach((rx) => {
      const a = amenities.find((x) => rx.test(x.title || '') && !picked.includes(x));
      if (a) picked.push(a);
    });
    ['Apartment', 'Community'].forEach((gr) => amenities.filter((a) => a.group === gr && !picked.includes(a)).forEach((a) => picked.length < 6 && picked.push(a)));
  }
  const shownAmenities = featuredAmenities.length ? picked : picked.slice(0, 6);
  const groups: Record<string, Amenity[]> = {};
  shownAmenities.forEach((a) => (groups[groupLabel(a.group)] = groups[groupLabel(a.group)] || []).push(a));
  const groupNames = Object.keys(groups).sort((a) => (a === 'Community' ? -1 : 1));

  const chosen = photoSlot.image && photoSlot.image !== photos[0]?.image ? { src: photoSlot.image, alt: photoSlot.alt || '' } : null;
  const base = photos[6] || photos[0];
  // A photo slot naming an amenity follows that amenity; blank-named slots fill the remaining rows in order.
  const norm = (s?: string | null) => (clean(s) || '').trim().toLowerCase();
  const rowSlot: (number | null)[] = shownAmenities.map(() => null);
  photoSlots.forEach((s, k) => {
    const name = norm(s.amenity);
    const r = name ? shownAmenities.findIndex((a, i) => rowSlot[i] == null && norm(a.title) === name) : -1;
    if (r >= 0) rowSlot[r] = k;
  });
  const blankSlots = photoSlots.flatMap((s, k) => (norm(s.amenity) ? [] : [k]));
  shownAmenities.forEach((_, i) => {
    if (rowSlot[i] == null && blankSlots.length) rowSlot[i] = blankSlots.shift()!;
  });

  // Picked photos first, then each amenity's own or gallery match, then unused gallery photos, so rows don't share a photo.
  const rowPhotos = shownAmenities.map((a, i): { src: string; alt: string } | null => {
    const s = rowSlot[i] != null ? photoSlots[rowSlot[i]!] : null;
    const img = clean(s?.image);
    return img ? { src: img, alt: clean(s?.alt) || a.title || '' } : null;
  });
  const used = new Set(rowPhotos.flatMap((ph) => (ph ? [ph.src] : [])));
  shownAmenities.forEach((a, i) => {
    if (rowPhotos[i]) return;
    const ph = amenityPhoto(a, photos.filter((x) => !used.has(x.image || '')));
    if (ph) used.add((rowPhotos[i] = ph).src);
  });
  const sectionSrc = chosen?.src || base?.image;
  shownAmenities.forEach((a, i) => {
    if (rowPhotos[i]) return;
    const spare = photos.find((x) => x.image && x.image !== sectionSrc && !used.has(x.image));
    if (spare?.image) used.add((rowPhotos[i] = { src: spare.image, alt: spare.alt_text || a.title || '' }).src);
  });

  const activePhoto = active != null ? rowPhotos[active] : null;
  const featured = clean(p.property_featured_image);
  const shown = activePhoto || chosen || (base ? { src: base.image, alt: base.alt_text || '' } : featured ? { src: featured, alt: '' } : null);
  const lbItems = shownAmenities.flatMap((a, i) => {
    const ph = rowPhotos[i];
    return ph ? [{ ...ph, caption: a.title }] : [];
  });

  const pick = (i: number) => {
    setActive(i);
    if (touch) {
      const src = rowPhotos[i]?.src;
      lb.open(Math.max(0, lbItems.findIndex((x) => x.src === src)));
    }
  };

  if (!shownAmenities.length) return null;

  return (
    <section className="section amenities" id="amenities">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="amenitieslist.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="amenitieslist.title" as="h2" className="h2" defaultValue={titleDefault} />
        </div>
        <RichTextContent path="amenitieslist.lead" as="p" className="section__lead" defaultValue={leadDefault} />
      </div>
      <div className="flex items-stretch gap-16 max-[1199px]:flex-col">
        <EditableSlotGroup
          groupPath="amenitieslist.photo"
          definition={photoDef}
          className={shown ? 'photo photo--tall [&_img]:opacity-100 [&_img]:[transition:opacity_var(--q-motion-slow)_var(--q-ease-in-out)]' : ''}
        >
          {shown && <VibesitesImage key={shown.src} url={shown.src} alt={shown.alt} sizes="(min-width: 1200px) 592px, 100vw" />}
        </EditableSlotGroup>
        <EditableCollectionBinding bindingPath="amenitieslist.items" label="Amenities" className="flex grow flex-col gap-10">
          {groupNames.map((g, gi) => (
            <div className="flex flex-col" key={g} data-reveal style={stagger(gi + 1)}>
              <div className="eyebrow border-b border-b-[--q-secondary] pb-3">{g}</div>
              {groups[g].map((a) => {
                const i = shownAmenities.indexOf(a);
                const ph = rowPhotos[i];
                return wrapPhoto(rowSlot[i], i, ph ? (
                  <button
                    className={`group/row flex w-full cursor-pointer items-center gap-6 rounded-none border-b border-b-[--q-border] py-3 text-left font-heading text-[22px] leading-7 [background:none] [border-left:0] [border-right:0] [border-top:0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[--q-primary-on-paper] max-[767px]:py-2 max-[767px]:text-[19px] max-[767px]:leading-[25px] ${
                      active === i ? 'text-[--q-primary-on-paper]' : 'text-inherit hover:text-[--q-primary-on-paper]'
                    }`}
                    type="button"
                    aria-label={`${a.title}: show the photo`}
                    onClick={() => pick(i)}
                    onMouseEnter={() => !touch && setActive(i)}
                    onFocus={() => !touch && setActive(i)}
                  >
                    <span className="grow">{a.title}</span>
                    <VibesitesImage
                      url={ph.src}
                      alt=""
                      sizes="52px"
                      className={`h-9 w-[52px] shrink-0 rounded-sm object-cover outline outline-2 outline-offset-2 [transition:outline-color_var(--q-motion-fast),transform_var(--q-motion-normal)_var(--q-ease-out)] motion-reduce:[transition:none] max-[767px]:h-[35px] max-[767px]:w-12 ${
                        active === i
                          ? '[transform:scale(1.06)] outline-[--q-primary-on-paper]'
                          : 'outline-transparent group-hover/row:outline-[--q-primary-on-paper] group-hover/row:[transform:scale(1.06)]'
                      }`}
                    />
                  </button>
                ) : (
                  <div className="flex items-baseline gap-6 border-b border-b-[--q-border] py-4 font-heading text-[22px] leading-7 max-[767px]:py-[13px] max-[767px]:text-[19px] max-[767px]:leading-[25px]">
                    <span className="grow">{a.title}</span>
                  </div>
                ));
              })}
            </div>
          ))}
        </EditableCollectionBinding>
      </div>
      <div className="section__cta" data-reveal>
        <div className="section__buttons">
          <EditableSlotGroup groupPath="amenitieslist.cta" definition={ctaDef}>
            <a className="btn btn-primary" href={cta.url || PATHS.amenities} target={cta._target || '_self'}>
              {cta.label || 'See All Amenities'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      <Lightbox items={lbItems} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities',
  tokens: {
    'amenitieslist.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Amenities · your home and the community</p>' },
    'amenitieslist.title': {
      label: 'Title',
      description: 'Until edited, shows part of the blurb (its bold first sentence for a title, the rest for a lead). Pick the blurb field from the suggestions to show it in full.',
      type: 'richtext',
      defaultValue: '<p>Everyday comfort, built in</p>',
      suggestedFields: ['property_home_amenities_blurb', 'property_amenities_blurb'],
      generator: {
        prompt: "A short headline for this property's amenities, true to what it actually offers. No closing period.",
        context: 'H2 of the Amenities section on the home page, above the lead and the shownAmenities-amenity list.',
        length: '3–7 words',
      },
    },
    'amenitieslist.lead': {
      label: 'Lead',
      description: 'Until edited, shows part of the blurb (its bold first sentence for a title, the rest for a lead). Pick the blurb field from the suggestions to show it in full.',
      type: 'richtext',
      defaultValue: '',
      suggestedFields: ['property_home_amenities_blurb', 'property_amenities_blurb'],
      generator: {
        prompt: 'A lead that sums up what living with these amenities is like, in the home and around the community. Name only amenities this property really has. Point readers to the amenities page if it exists.',
        context: 'Lead under the Amenities headline on the home page, beside the shownAmenities-amenity list. Do not repeat the headline.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'amenitieslist.photo': photoDef,
    'amenitieslist.amenity_photo_1': amenityPhoto1Def,
    'amenitieslist.amenity_photo_2': amenityPhoto2Def,
    'amenitieslist.amenity_photo_3': amenityPhoto3Def,
    'amenitieslist.amenity_photo_4': amenityPhoto4Def,
    'amenitieslist.amenity_photo_5': amenityPhoto5Def,
    'amenitieslist.amenity_photo_6': amenityPhoto6Def,
    'amenitieslist.amenity_photo_7': amenityPhoto7Def,
    'amenitieslist.amenity_photo_8': amenityPhoto8Def,
    'amenitieslist.amenity_photo_9': amenityPhoto9Def,
    'amenitieslist.amenity_photo_10': amenityPhoto10Def,
    'amenitieslist.amenity_photo_11': amenityPhoto11Def,
    'amenitieslist.amenity_photo_12': amenityPhoto12Def,
    'amenitieslist.cta': ctaDef,
  },
  collectionBindings: {
    'amenitieslist.items': {
      label: 'Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
